import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from "react";
import { observer } from "mobx-react";
import { Form, Checkbox, Spin, App, Input, Divider } from "antd";
import { __, request, urlBuilder } from "../../../utils";
import { locationRestExportGet } from "../../../wp-api";
import { useStores } from "../../../store";
import { reactNl2Br, copyToClipboard } from "@devowl-wp/react-utils";
const LAYOUT = {
    labelCol: {
        span: 24
    },
    wrapperCol: {
        span: 24
    }
};
const ExportForm = observer(()=>{
    const { message } = App.useApp();
    const { optionStore: { isTcf, others: { hints: { export: exportHints } } } } = useStores();
    const [form] = Form.useForm();
    const [isBusy, setBusy] = useState(false);
    const [result, setResult] = useState("");
    const initialValues = {
        settings: true,
        cookieGroups: true,
        cookies: true,
        blocker: true,
        tcfVendorConfigurations: true,
        customizeBanner: true
    };
    // Request export data from REST API
    const handleFinish = useCallback(async (values)=>{
        setBusy(true);
        try {
            setResult(JSON.stringify(await request({
                location: locationRestExportGet,
                params: values
            })));
        } finally{
            setBusy(false);
        }
    }, []);
    // Copy the export to clipboard automatically on click textarea
    const handleCopyTextarea = useCallback(()=>{
        copyToClipboard(result);
        message.success(__("Export successfully copied to the clipboard."));
    }, [
        result
    ]);
    // Download export as JSON
    const handleDownloadButton = useCallback((e)=>{
        (async ()=>{
            setBusy(true);
            window.location.href = urlBuilder({
                location: locationRestExportGet,
                params: {
                    ...form.getFieldsValue(),
                    download: true
                }
            });
            setBusy(false);
        })();
        e.preventDefault();
        return false;
    }, [
        setBusy,
        form
    ]);
    return /*#__PURE__*/ _jsx(Spin, {
        spinning: isBusy,
        children: /*#__PURE__*/ _jsxs(Form, {
            name: "export",
            form: form,
            ...LAYOUT,
            initialValues: initialValues,
            onFinish: handleFinish,
            labelWrap: true,
            children: [
                /*#__PURE__*/ _jsxs(Form.Item, {
                    label: __("Content to export"),
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(Form.Item, {
                                name: "settings",
                                noStyle: true,
                                valuePropName: "checked",
                                children: /*#__PURE__*/ _jsx(Checkbox, {
                                    children: __("Settings")
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(Form.Item, {
                                name: "cookieGroups",
                                noStyle: true,
                                valuePropName: "checked",
                                children: /*#__PURE__*/ _jsx(Checkbox, {
                                    children: __("Service groups")
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(Form.Item, {
                                name: "cookies",
                                noStyle: true,
                                valuePropName: "checked",
                                children: /*#__PURE__*/ _jsx(Checkbox, {
                                    children: __("Services")
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(Form.Item, {
                                name: "blocker",
                                noStyle: true,
                                valuePropName: "checked",
                                children: /*#__PURE__*/ _jsx(Checkbox, {
                                    children: __("Content Blocker")
                                })
                            })
                        }),
                        isTcf && /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(Form.Item, {
                                name: "tcfVendorConfigurations",
                                noStyle: true,
                                valuePropName: "checked",
                                children: /*#__PURE__*/ _jsx(Checkbox, {
                                    children: __("TCF Vendor configurations")
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(Form.Item, {
                                name: "customizeBanner",
                                noStyle: true,
                                valuePropName: "checked",
                                children: /*#__PURE__*/ _jsx(Checkbox, {
                                    children: __("Cookie banner customization")
                                })
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Form.Item, {
                    children: [
                        /*#__PURE__*/ _jsx("input", {
                            type: "submit",
                            className: "button button-primary",
                            style: {
                                marginTop: 10
                            },
                            value: __("Export")
                        }),
                        /*#__PURE__*/ _jsx("input", {
                            onClick: handleDownloadButton,
                            type: "submit",
                            className: "button",
                            style: {
                                margin: "10px 0 0 10px"
                            },
                            value: __("Download JSON")
                        }),
                        exportHints.length > 0 && /*#__PURE__*/ _jsx("div", {
                            className: "notice notice-info inline below-h2 notice-alt",
                            style: {
                                margin: "10px 0 0 0"
                            },
                            children: /*#__PURE__*/ _jsx("p", {
                                children: reactNl2Br(exportHints.join("\n\n"))
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Form.Item, {
                    style: {
                        display: result ? "block" : "none"
                    },
                    children: [
                        /*#__PURE__*/ _jsxs(Form.Item, {
                            noStyle: true,
                            children: [
                                /*#__PURE__*/ _jsx(Divider, {
                                    style: {
                                        marginTop: 0
                                    },
                                    children: __("Result")
                                }),
                                /*#__PURE__*/ _jsx(Input.TextArea, {
                                    onClick: handleCopyTextarea,
                                    value: result,
                                    readOnly: true,
                                    rows: 5
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "description",
                            children: __('Copy the exported content and paste it into the "Import" text area of your target WordPress installation.')
                        })
                    ]
                })
            ]
        })
    });
});
export { ExportForm };
